import { compareAsc, compareDesc } from "date-fns";

import { Capability, hasSectionCapability } from "../../shared/capabilities";

import { createAppSelector } from "./base";
import { selectCurrentUserComputedRole } from "./permissions";

import { RootState } from "store";
import { AIContextItem } from "store/slices/ai.ts";

export const selectAiChatMessages = createAppSelector(
  [(state) => state.ai.aiChatMessages],
  (aiChatMessages) =>
    Object.values(aiChatMessages).toSorted((a, b) => {
      // Put system messages before other messages and sort by createdAt.
      if (a.role === "system" && b.role !== "system") return -1;
      if (a.role !== "system" && b.role === "system") return 1;
      return compareDesc(b.createdAt, a.createdAt);
    }),
);

export const selectContextMessages = createAppSelector(
  [(state) => state.ai.contextItems],
  (contextItems): AIContextItem[] => {
    const contextMessages: AIContextItem[] = Object.values(contextItems || {});
    return contextMessages.toSorted((a, b) => {
      return b.role.localeCompare(a.role) || compareAsc(b.id, a.id);
    });
  },
);

export const selectLastAiContextParts = (state: RootState) => state.ai.lastAiContextParts;

export const selectToolNames = (state: RootState) => state.ai.tools;

export const selectIsAiDebuggerVisible = (state: RootState) => state.ai.aiDebuggerVisible;

export const selectIsAssessWithAIModeEnabled = createAppSelector(
  [(state) => state.ai.assessWithAIMode, selectCurrentUserComputedRole],
  (assessWithAIMode, computedRole) =>
    assessWithAIMode && hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION),
);

export const selectIsRecordingFeedbackStyle = (state: RootState) =>
  state.ai.isRecordingFeedbackStyle;

export const selectFeedbackStyleSampleCount = (state: RootState) =>
  state.ai.assessmentFeedbackStyleSamples?.length ?? 0;

export const selectIsDerivingFeedbackStyle = (state: RootState) => state.ai.isDerivingFeedbackStyle;

export const selectIsStudentResponsesForAIEnabled = (state: RootState) =>
  state.ai.isStudentResponsesForAIEnabled;

export const selectWorksheetChatDocViewAccessId = (state: RootState) =>
  state.ai.worksheetChatDocViewAccessId;

export const selectUserSelectedAIModel = (state: RootState) => state.ai.userSelectedAIModel;
